import Modal from '../Modal/Modal';
import { useMetadata } from '../../context/MetadataContext';
import { usePuzzleLeaderboard } from '../../hooks/usePuzzleLeaderboard';
import CountryFlag from '../../utils/CountryFlag';
import formatTime from '../../utils/formatTime';
import styles from './PuzzleLeaderboard.module.css';
import FormattedTime from '../../utils/FormattedTime';

const PuzzleLeaderboard = ({ isOpen, onClose }) => {
  const { getText } = useMetadata();
  const {
    leaderboardData,
    isLoading,
    hasMore,
    activeCategory,
    activeTimeframe,
    changeCategory,
    changeTimeframe,
    loadMore,
  } = usePuzzleLeaderboard(isOpen);

  const categories = [
    { value: 'global', label: getText('common_text.worldwide')  },
    { value: 'country', label: getText('common_text.country')  },
    { value: 'gender', label: getText('common_text.gender')  },
    { value: 'age', label: getText('common_text.age_group') }
  ];

  const timeframes = [
    { value: 'all_time', label: getText('leaderboard.all_time') },
    { value: 'this_year', label: getText('leaderboard.this_year') },
    { value: '30_days', label: getText('leaderboard.last_30') },
    { value: '7_days', label: getText('leaderboard.last_7') }
  ];

  const handleCategoryChange = changeCategory;

  const handleTimeframeChange = changeTimeframe;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getText('common_text.leaderboard')}
      className={styles.puzzleLeaderboardModal}
    >
      <div className={styles.content}>
        {/* Selection Controls */}
        <div className={styles.selectionRow}>
          <select 
            className={styles.selectDropdown}
            value={activeCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>

          <select 
            className={styles.selectDropdown}
            value={activeTimeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
          >
            {timeframes.map((timeframe) => (
              <option key={timeframe.value} value={timeframe.value}>
                {timeframe.label}
              </option>
            ))}
          </select>
        </div>

        {isLoading && leaderboardData.length === 0 ? (
          <div className={styles.loading}>
            {getText('common_text.loading')}
          </div>
        ) : leaderboardData.length === 0 ? (
          <div className={styles.noData}>
            {getText('solo_play.no_leaderboard_data')}
          </div>
        ) : (
          <div className={styles.leaderboardList}>
            <div className={styles.leaderboardHeader}>
              <span className={styles.rankHeader}>{getText('common_text.rank')}</span>
              <span className={styles.nicknameHeader}>{getText('common_text.nickname')}</span>
              <span className={styles.timeHeader}>{getText('common_text.time')}</span>
            </div>
            {leaderboardData.map((leader, index) => (
              <div key={`${leader.rank}-${leader.nickname}-${index}`} className={styles.leaderboardRow}>
                <span className={styles.rank}>{leader.rank}</span>
                <span className={styles.nickname}>
                  {leader.nickname}
                  {leader.country_code && (
                    <CountryFlag 
                      countryCode={leader.country_code} 
                      style={{ marginLeft: '8px', width: '1em', height: '1em' }}
                    />
                  )}
                </span>
                <span className={styles.totalTime}><FormattedTime seconds={leader.total_time} /></span>
              </div>
            ))}
            
            {/* Load More Button */}
            {hasMore && (
              <div className={styles.loadMoreContainer}>
                <button 
                  className={styles.loadMoreBtn} 
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? getText('common_text.loading') : getText('common_text.load_more')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PuzzleLeaderboard;